import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { getBusinessPlansTableData, getBusinessTypes, getTerminals } from '../../services/commerce.service';
import {
  AddCommerceSixthAndFinalStepLocationState,
  BusinessTableChunk,
  Contract,
  FinalStepAttachments,
  InfoBlockField,
  NewBeneficiaryValues,
  NewBranchValues,
} from '../../types/add_commerce.types';
import { FormStatusEnum } from '../../types/enums/form-status.enum';
import {
  bankAccountFields,
  banks,
  basicInfoFields,
  economicActivityFields,
  laws,
  mainAddressFields,
  tributaryInfoFields,
} from '../../utils/data';
import {
  filterEmptyOptionalAddressFields,
  formatTableData,
  getBeneficiaries,
  getCurrencyName,
  getEnumName,
  getEnumTypeValue,
  isEmptyField,
  updateBlockFields,
  updateDuplicatedBlockFields,
} from '../../utils/helpers';
import { getStatusValue } from '../../utils/statusHelpers';
import IncompleteOnboardingSummary from '../IncompleteOnboardingSummary/IncompleteOnboardingSummary';
import Attachments from './Attachments';
// import ECommerceBlock from './ECommerceBlock';
import InfoBlock from './InfoBlock';
import ProductsBlock from './ProductsBlock';
import ShareBlock from './ShareBlock';
import TariffBlock from './TariffBlock';

interface BusinessInfoProps {
  businessData: AddCommerceSixthAndFinalStepLocationState;
  showAttachments: boolean;
  setCheckboxState?: Dispatch<SetStateAction<boolean>>;
  checkboxState?: boolean;
  setAnticipatePayments?: Dispatch<SetStateAction<boolean>>;
  ecommerceAndTariffsForm?: any;
  anticipatePayments?: boolean;
  comment?: string;
  commerceId?: any;
  status?: FormStatusEnum | null;
  comesFromSearchCommerce?: boolean;
  handleStatus?: () => void;
  uploadedContract?: boolean;
  setIsLoading?: any;
  onOnboardingDeleted?: () => void;
}

function BusinessInfo({
  businessData,
  showAttachments = false,
  setCheckboxState,
  checkboxState,
  setAnticipatePayments,
  anticipatePayments,
  comment,
  commerceId,
  status = null,
  comesFromSearchCommerce = false,
  ecommerceAndTariffsForm,
  handleStatus,
  uploadedContract,
  setIsLoading,
  onOnboardingDeleted,
}: BusinessInfoProps) {
  const [tableData, setTableData] = useState<Array<BusinessTableChunk>>([]);
  const [tableDataError, setTableDataError] = useState<string>('');
  const [terminalCatalog, setTerminalCatalog] = useState<any[]>([]);
  const [businessTypeName, setBusinessTypeName] = useState<string>('');

  const getTableData = async () => {
    const response = await getBusinessPlansTableData(
      getEnumTypeValue('taxPayer', businessData.taxpayerType!),
      getEnumTypeValue('businessCategory', businessData.businessCategory!),
    );

    if (response.result) {
      const formattedTableData = formatTableData(response.data);
      setTableData(formattedTableData);
    } else {
      setTableDataError(response?.data?.showMessage?.ES);
    }
  };

  console.log(businessData);

  useEffect(() => {
    if (!comesFromSearchCommerce) {
      void getTableData();
    }
    const terminalsBusinessId = commerceId || businessData?.id || (businessData as any)?.businessId;
    const terminalsTaxpayerTypeId = businessData?.taxpayerType?.id ?? (businessData as any)?.taxpayerTypeId;
    if (
      terminalsBusinessId &&
      Number.isFinite(Number(terminalsBusinessId)) &&
      terminalsTaxpayerTypeId != null
    ) {
      getTerminals(String(terminalsBusinessId))
        .then((data) => {
          if (Array.isArray(data?.data)) setTerminalCatalog(data.data);
        })
        .catch(() => setTerminalCatalog([]));
    }
    if (businessData?.businessCategoryId && businessData?.businessType) {
      getBusinessTypes(+businessData.businessCategoryId)
        .then((response) => {
          const match = response?.data?.find((bt: any) => bt.value === +businessData.businessType);
          if (match) setBusinessTypeName(match.name);
        })
        .catch(() => setBusinessTypeName(''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const beneficiaries = getBeneficiaries(businessData?.branches);

  const hasUnvalidatedBankAccount = businessData.bankAccounts?.some((bankAccount) => !bankAccount.validated);

  const pendingValidationBox = (
    <div className="pending-validation-box">
      <FontAwesomeIcon icon={faCircleExclamation as IconProp} />
      <p>La cuenta no pudo validarse automáticamente. Se validará en las próximas horas.</p>
    </div>
  );

  const attachments: FinalStepAttachments = {
    cardFormDGI: businessData?.cardFormDGI! as string,
    beneficiaries,
    extraDocuments: [
      businessData?.extraDocumentOne as string,
      businessData?.extraDocumentTwo as string,
      businessData?.extraDocumentThree as string,
    ],
  };

  console.log(businessData);

  const basicInfoValues = [
    businessData?.rut,
    businessData?.fantasyName,
    businessData?.socialReason,
    `${businessData?.contactFirstName} ${businessData?.contactLastName}`,
    businessData?.phone,
    businessData?.cellphone,
    businessData?.email,
    businessData?.businessCategory?.name,
    businessTypeName,
    businessData?.taxpayerType?.description,
  ];

  const mainAddressValues = [
    businessData?.city?.state?.name,
    businessData?.city?.name,
    `${businessData?.addressStreet} ${businessData?.addressNumber}`,
    isEmptyField(businessData?.addressFlatNumber),
    isEmptyField(businessData?.addressSolar),
    isEmptyField(businessData?.addressPlot),
    businessData?.averageMonthlyBilling,
  ];

  const bankAccountValues = [
    banks.find((bank) => bank.id === businessData?.bankExternalId)?.name,
    businessData?.accountNumber,
    getEnumName('currency', String(businessData?.accountCurrency)),
    businessData?.accountBranch,
    businessData?.accountHolder,
  ];

  const lawValues = [
    businessData?.law_19210,
    businessData?.law_17934,
    businessData?.law_18083,
    businessData?.law_18910,
  ];

  const basicInfoUpdated = updateBlockFields(basicInfoFields, basicInfoValues);
  const bankAccountUpdatedFields = updateBlockFields(bankAccountFields, bankAccountValues);
  const mainAddressUpdated = filterEmptyOptionalAddressFields(
    updateBlockFields(mainAddressFields, mainAddressValues),
  );
  const tributaryInfoUpdated = updateBlockFields(
    tributaryInfoFields,
    businessData?.taxpayerType?.description,
  );
  const economicActivityUpdated = updateBlockFields(
    economicActivityFields,
    businessData?.businessCategory?.name,
  );

  const lawsUpdated: InfoBlockField[] = updateBlockFields(
    laws.map((law) => law.name),
    lawValues,
  );

  let terminalsUpdated: InfoBlockField[] = terminalCatalog
    .filter((terminal: any) => {
      const saved = businessData?.terminals?.find((t) => +t.type === terminal.id);
      return saved && Number(saved.quantity) > 0;
    })
    .map((terminal: any) => {
      const saved = businessData?.terminals?.find((t) => +t.type === terminal.id);
      return {
        name: terminal.epos ? `${terminal.name} (EPOS)` : terminal.name,
        value: saved?.quantity,
      };
    });

  if ((businessData as any).electronicInvoicing) {
    const ei = {
      name: 'Facturación electrónica',
      value: 'Sí',
    };

    terminalsUpdated = [ei, ...terminalsUpdated];
  }

  const contractsUpdated = updateBlockFields(['Contratos enviados a firmar', ''], ['Getnet', 'NAD']); // TODO: check with backend

  const contractsSigned: boolean | undefined = businessData?.contracts?.every(
    (contract: Contract) => contract.vaFirmaStatus === 'SIGNED' || contract.vaFirmaStatus === 'NOT_SENDED',
  );

  // E-COMMERCE block intentionally hidden on the resume-onboarding screen (kept for reference).
  // To be restored once the post-onboarding product sale flow moves forward.
  // const showECommerceBlock: boolean = !!(
  //   contractsSigned &&
  //   !businessData?.hasEcommerce &&
  //   businessData?.principalEmail
  // );

  // The ESTADO badge buckets any status it doesn't recognize (missing bank account, missing
  // terminals, an early/abandoned onboarding with no granular status yet, etc.) under "Sin
  // completar" — mirror that exact check here so this summary matches whatever the badge shows,
  // regardless of the coarser `status`/step values used for the rest of the wizard's navigation
  const isIncompleteStatus = getStatusValue(businessData.status) === 'Sin completar';

  return (
    <>
      {comesFromSearchCommerce ? (
        <>
          {isIncompleteStatus ? (
            <IncompleteOnboardingSummary businessData={businessData} onDeleted={onOnboardingDeleted} />
          ) : (
            <>
              {(status === FormStatusEnum.IN_PROCESS ||
                status === FormStatusEnum.FINISHED ||
                status === FormStatusEnum.STARTED_BY_OTHER) && (
                <InfoBlock title="Información básica del comercio" fields={basicInfoUpdated} />
              )}
              {(status === FormStatusEnum.IN_PROCESS || status === FormStatusEnum.FINISHED) &&
                !contractsSigned && (
                  <>
                    <InfoBlock title="Contratos" fields={contractsUpdated} />
                    {!uploadedContract && (
                      <ShareBlock
                        email={businessData?.principalEmail!}
                        commerceId={commerceId}
                        rut={String(businessData.rut)}
                        handleStatus={handleStatus}
                        canDelete={businessData.status !== FormStatusEnum[FormStatusEnum.FINISHED]}
                        onDeleted={onOnboardingDeleted}
                      />
                    )}
                  </>
                )}
              {/* {showECommerceBlock && (
                <ECommerceBlock
                  title="E-Commerce"
                  formRef={ecommerceAndTariffsForm}
                  businessSector={businessData.businessCategory?.externalId}
                  businessId={businessData.id}
                  setIsLoading={setIsLoading}
                />
              )} */}
            </>
          )}
        </>
      ) : (
        <>
          <InfoBlock title="Información básica del comercio" fields={basicInfoUpdated} />
          {businessData?.branches?.map((branch: NewBranchValues, index: number) => {
            const updatedFields = filterEmptyOptionalAddressFields(
              updateDuplicatedBlockFields('branch', branch),
            );
            return (
              <InfoBlock
                title={`Sucursal ${index + 1}`}
                fields={updatedFields}
                key={index}
                isLastLoop={index === businessData?.branches.length - 1}
                borderNone={index !== businessData?.branches.length - 1}
              />
            );
          })}
          {beneficiaries?.map((beneficiary: NewBeneficiaryValues, index: number) => {
            const updatedFields = updateDuplicatedBlockFields('beneficiary', beneficiary);
            const branchName =
              beneficiary.branchOfficeId && beneficiary.branchOfficeId > 0
                ? (businessData.branches?.find((b) => b.id === beneficiary.branchOfficeId)?.fantasyName ??
                  'Todas')
                : 'Todas';
            return (
              <InfoBlock
                title={`Beneficiario final ${index + 1}`}
                fields={[...updatedFields, { name: 'Sucursal', value: branchName }]}
                key={index}
                isLastLoop={index === beneficiaries.length - 1}
              />
            );
          })}
          <InfoBlock title="Domicilio principal" fields={mainAddressUpdated} />
          {businessData.bankAccounts[0] ? (
            <>
              {businessData.bankAccounts.map((bankAccount, index) => {
                if (!bankAccount.validated) return null;

                const bankAccountValues = [
                  banks.find((bank) => bank.id === bankAccount?.bankExternalId)?.name,
                  bankAccount?.accountNumber,
                  getCurrencyName(+bankAccount?.accountCurrency),
                  bankAccount?.accountBranch,
                  bankAccount?.accountHolder,
                ];

                return (
                  <InfoBlock
                    key={index}
                    title={`Cuenta bancaria ${index + 1}`}
                    fields={updateBlockFields(bankAccountFields, bankAccountValues)}
                    isLastLoop={index === businessData.bankAccounts.length - 1 && !hasUnvalidatedBankAccount}
                  />
                );
              })}
              {hasUnvalidatedBankAccount && (
                <InfoBlock title="Cuenta bancaria" isLastLoop>
                  {pendingValidationBox}
                </InfoBlock>
              )}
            </>
          ) : (
            <InfoBlock
              title="Cuenta bancaria"
              fields={updateBlockFields(
                ['Número de seguimiento Santander'],
                [businessData.accountTrackingNumber],
              )}
            >
              {pendingValidationBox}
            </InfoBlock>
          )}
          <TariffBlock
            isStandarTariff={businessData?.standard}
            tariffType={businessData?.tariffType}
            customTariff={businessData?.tariff}
            businessTaxPayerType={businessData.taxpayerType!}
            acceptMaster={businessData.acceptMaster}
            acceptVisa={businessData.acceptVisa}
            businessCategory={businessData.businessCategory}
            currency={
              businessData?.currency
                ? getCurrencyName(
                    (Number.isNaN(Number(businessData.currency))
                      ? businessData.currency
                      : Number(businessData.currency)) as any,
                  )
                : undefined
            }
            anticipatePayments={businessData?.anticipatePayments}
          />
          <ProductsBlock businessData={businessData} terminalsUpdated={terminalsUpdated} />
          <Attachments attachments={attachments} showAttachments />
        </>
      )}
    </>
  );
}

export default BusinessInfo;
